// Builds image URLs served by the /cdn Function.
//
// In production we wrap them in Cloudflare Image Resizing via the /cdn-cgi/image/
// prefix, which the edge intercepts to resize/recompress before hitting origin.
// That prefix is a no-op outside the Cloudflare edge (e.g. local wrangler), so
// on localhost we skip it and serve originals — identical behaviour, just heavier.

const RESIZE_ENABLED = !isLocalHost();

// Flipped on if a resized request ever fails (e.g. Image Transformations isn't
// enabled on this hostname, such as a *.pages.dev URL). Once set, we stop
// generating resize URLs and just serve originals.
let resizeUnavailable = false;

export function markResizeUnavailable(): void {
  resizeUnavailable = true;
}

function isLocalHost(): boolean {
  if (typeof location === 'undefined') return false;
  const h = location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h === '0.0.0.0';
}

/**
 * @param filename flat R2 object name from the catalog (e.g. "sunset001.jpg")
 * @param width    target render width in CSS px; scaled by DPR and capped below
 */
export function imageUrl(filename: string, width?: number): string {
  const origin = `/cdn/${encodeURIComponent(filename)}`;
  if (!RESIZE_ENABLED || resizeUnavailable || !width) return origin;

  const opts = [
    `width=${clampWidth(width)}`,
    'quality=82',
    'format=auto',
    'fit=scale-down',
  ].join(',');
  return `/cdn-cgi/image/${opts}${origin}`;
}

/** Pick a sensible source width for a full-screen image given viewport + DPR. */
export function viewportImageWidth(): number {
  if (typeof window === 'undefined') return 1600;
  const dpr = Math.min(window.devicePixelRatio || 1, 2); // cap DPR at 2
  return Math.round(window.innerWidth * dpr);
}

// Round up to a small set of widths so the edge cache gets reused across
// similar viewports instead of caching a unique variant per pixel width.
function clampWidth(width: number): number {
  const buckets = [640, 960, 1280, 1600, 2048, 2560, 3200];
  for (const b of buckets) if (width <= b) return b;
  return buckets[buckets.length - 1];
}
